import { useEffect, useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';
import { registerDialog } from '../../lib/dialogStack';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: ReactNode;
    subtitle?: ReactNode;
    icon?: React.ElementType;
    children: ReactNode;
    /** Footer pinned to the bottom (e.g. primary action). Optional. */
    footer?: ReactNode;
    /** Tailwind max-width class for the panel. Default: max-w-lg */
    maxWidth?: string;
    /**
     * Hand the whole panel to the content: no padding, no scroll region of the
     * modal's own, and a definite height so the content can lay out a sticky
     * header, its own scroller and a pinned footer. For editors that already
     * own that structure (QuoteBuilder).
     */
    flush?: boolean;
    /** Consistent workspace for appointment, diagnostic, quote and job editors. */
    editor?: boolean;
    /**
     * False while the content has its own overlay open that is not a dialog in
     * the stack (QuoteBuilder's email and preview), so Escape leaves the modal
     * alone. Default true.
     */
    closeOnEscape?: boolean;
}

/**
 * Reusable modal shell in the ForgeDash `--fd-*` design language.
 *
 * Replaces the app's ad-hoc 3D card flips and one-off `fixed inset-0` modals
 * with one consistent surface. The panel is always a centred dialog — never a
 * bottom sheet — and is inset from every edge so it can't collide with the
 * mobile bottom nav or run off-screen. Content scrolls inside its own region so
 * the page body never scrolls horizontally and long numbers/tables never
 * overflow.
 */
export const Modal = ({
    isOpen,
    onClose,
    title,
    subtitle,
    icon: Icon,
    children,
    footer,
    maxWidth = 'max-w-lg',
    flush = false,
    editor = false,
    closeOnEscape = true,
}: ModalProps) => {
    // Read through refs so the effect below keys on `isOpen` alone. Callers pass
    // inline handlers; re-running on every render would re-register this modal
    // and lift it back to the top of the dialog stack, over a confirmation that
    // was opened above it.
    const onCloseRef = useRef(onClose);
    const closeOnEscapeRef = useRef(closeOnEscape);
    useEffect(() => {
        onCloseRef.current = onClose;
        closeOnEscapeRef.current = closeOnEscape;
    });

    // Lock body scroll + close on Escape while open — only when this modal is
    // the topmost dialog (lib/dialogStack).
    useEffect(() => {
        if (!isOpen) return;
        const dialog = registerDialog();
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape' && dialog.isTop() && closeOnEscapeRef.current) onCloseRef.current();
        };
        document.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            dialog.release();
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [isOpen]);

    return createPortal(
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden
                        px-4 pt-[max(1.5rem,env(safe-area-inset-top))]
                        pb-[calc(5rem+env(safe-area-inset-bottom))] lg:pb-6 print:hidden"
                    onClick={(e) => {
                        if (e.target === e.currentTarget) onClose();
                    }}
                >
                    {/* Backdrop — Midnight, not black, so the page reads as set
                        back rather than switched off. */}
                    <div className="absolute inset-0 bg-scrim/70 backdrop-blur-[3px]" />

                    {/* Panel */}
                    <motion.div
                        initial={{ y: 10, opacity: 0, scale: 0.985 }}
                        animate={{ y: 0, opacity: 1, scale: 1 }}
                        exit={{ y: 6, opacity: 0, scale: 0.99 }}
                        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                        role="dialog"
                        aria-modal="true"
                        aria-label={typeof title === 'string' ? title : undefined}
                        className={`relative w-full ${editor ? 'max-w-4xl' : maxWidth} max-h-full ${flush || editor ? 'h-full' : ''} flex flex-col
                            fd-panel
                            overflow-hidden`}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        {(title || Icon) && (
                            <div className="flex items-center gap-3 px-5 sm:px-6 py-4 border-b border-line shrink-0">
                                {Icon && (
                                    <div className="w-10 h-10 shrink-0 grid place-items-center rounded-[var(--fd-r-md)] bg-gold-fill/10 border border-gold/35 text-gold">
                                        <Icon className="w-5 h-5" strokeWidth={2} />
                                    </div>
                                )}
                                <div className="flex-1 min-w-0">
                                    {title && (
                                        <h2 className="text-lg font-semibold font-display tracking-[-0.02em] text-fg truncate">
                                            {title}
                                        </h2>
                                    )}
                                    {subtitle && (
                                        <p className="text-xs text-fg-3 truncate">{subtitle}</p>
                                    )}
                                </div>
                                <button
                                    onClick={onClose}
                                    aria-label="Close"
                                    className="fd-btn fd-btn-icon -mr-2 shrink-0"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>
                        )}

                        {/* Scrollable content */}
                        <div className={flush
                            ? 'flex-1 min-h-0 flex flex-col'
                            : 'flex-1 overflow-y-auto overflow-x-hidden px-5 sm:px-6 py-5'}>
                            {children}
                        </div>

                        {/* Footer (optional) — safe-area aware on mobile */}
                        {footer && (
                            <div className="shrink-0 px-5 sm:px-6 py-4 border-t border-line bg-sunken/60">
                                {footer}
                            </div>
                        )}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body,
    );
};
